import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Autocomplete,
    FormControlLabel,
    Switch
} from '@mui/material';
import { guestPassAPI, memberProfileAPI } from '../../services/api';
import { GuestPass, CreateGuestPassRequest, GuestAccessValidation, MemberSponsorshipValidation } from '../../types/guestPass';

interface GuestPassDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (guestPass: GuestPass) => void;
    memberProfileId?: string; // If provided, will default to this member
    isAdminMode?: boolean; // If true, allows selecting any member
}

export const GuestPassDialog: React.FC<GuestPassDialogProps> = ({
    open,
    onClose,
    onSuccess,
    memberProfileId: initialMemberProfileId,
    isAdminMode = false
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestPhone, setGuestPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [memberProfileId, setMemberProfileId] = useState<string | undefined>(initialMemberProfileId);
    const [isComplimentary, setIsComplimentary] = useState(false);
    const [validationInfo, setValidationInfo] = useState<{
        guest?: GuestAccessValidation;
        member?: MemberSponsorshipValidation;
    }>({});
    const [members, setMembers] = useState<any[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    useEffect(() => {
        if (open) {
            // Reset form when dialog opens
            setGuestName('');
            setGuestEmail('');
            setGuestPhone('');
            setNotes('');
            setError(null);
            setValidationInfo({});
            setMemberProfileId(initialMemberProfileId);
            setIsComplimentary(false);

            // Load members for admin mode
            if (isAdminMode) {
                loadMembers();
            }
        }
    }, [open, initialMemberProfileId, isAdminMode]);

    const loadMembers = async () => {
        setLoadingMembers(true);
        try {
            const response = await memberProfileAPI.getAllMemberProfiles({ status: 'active' });
            setMembers(response.data);
        } catch (err: any) {
            console.error('Error loading members:', err);
        } finally {
            setLoadingMembers(false);
        }
    };

    const validateGuest = async (email: string) => {
        if (!email || !email.includes('@')) return;
        
        try {
            const response = await guestPassAPI.validateGuestAccess(email);
            setValidationInfo(prev => ({ ...prev, guest: response.data }));
        } catch (err: any) {
            console.error('Error validating guest:', err);
        }
    };

    const validateMember = async (memberId: string) => {
        if (!memberId) return;
        
        try {
            const response = await guestPassAPI.validateMemberSponsorship(memberId);
            setValidationInfo(prev => ({ ...prev, member: response.data }));
        } catch (err: any) {
            console.error('Error validating member:', err);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (guestEmail) {
                validateGuest(guestEmail);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [guestEmail]);

    useEffect(() => {
        if (memberProfileId) {
            validateMember(memberProfileId);
        }
    }, [memberProfileId]);

    const handleSubmit = async () => {
        if (!guestName || !guestEmail) {
            setError('Guest name and email are required');
            return;
        }

        if (!isComplimentary && !memberProfileId && isAdminMode) {
            setError('Please select a member to sponsor this guest or enable "Complimentary Guest Pass"');
            return;
        }

        if (!isComplimentary && validationInfo.guest && !validationInfo.guest.allowed) {
            setError(`Guest has reached maximum access limit (${validationInfo.guest.limit} passes in 30 days)`);
            return;
        }

        if (!isComplimentary && validationInfo.member && !validationInfo.member.allowed) {
            setError(`Member has reached maximum sponsorship limit (${validationInfo.member.limit} guests in 30 days)`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data: CreateGuestPassRequest = {
                memberProfileId: isComplimentary ? undefined : (isAdminMode ? memberProfileId : initialMemberProfileId),
                guestName,
                guestEmail,
                guestPhone: guestPhone || undefined,
                notes: notes || undefined,
                isComplimentary
            };

            const response = await guestPassAPI.create(data);
            onSuccess(response.data);
            onClose();
        } catch (err: any) {
            console.error('Error creating guest pass:', err);
            setError(err.response?.data?.message || 'Failed to create guest pass');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Generate Guest Pass</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {isAdminMode && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isComplimentary}
                                    onChange={(e) => {
                                        setIsComplimentary(e.target.checked);
                                        if (e.target.checked) {
                                            setMemberProfileId(undefined);
                                            setValidationInfo({});
                                        }
                                    }}
                                    disabled={loading}
                                />
                            }
                            label="Complimentary Guest Pass (No sponsor required)"
                            sx={{ mb: 2 }}
                        />
                    )}

                    {isAdminMode && !isComplimentary && (
                        <Autocomplete
                            options={members}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                            loading={loadingMembers}
                            value={members.find(m => m.id === memberProfileId) || null}
                            onChange={(event, newValue) => {
                                setMemberProfileId(newValue?.id);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Sponsoring Member"
                                    required
                                    helperText="Select the member who is sponsoring this guest"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{ mb: 2 }}
                        />
                    )}

                    {!isComplimentary && validationInfo.member && (
                        <Alert 
                            severity={validationInfo.member.allowed ? 'info' : 'error'} 
                            sx={{ mb: 2 }}
                        >
                            Member has sponsored {validationInfo.member.count} of {validationInfo.member.limit} guests in the last 30 days.
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Guest Name"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        required
                        disabled={loading}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Guest Email"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        required
                        disabled={loading}
                        sx={{ mb: 2 }}
                    />

                    {!isComplimentary && validationInfo.guest && (
                        <Alert 
                            severity={validationInfo.guest.allowed ? 'info' : 'error'} 
                            sx={{ mb: 2 }}
                        >
                            Guest has been granted {validationInfo.guest.count} of {validationInfo.guest.limit} passes in the last 30 days.
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Guest Phone (Optional)"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Notes (Optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={3}
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={
                        loading || 
                        !guestName || 
                        !guestEmail || 
                        (!isComplimentary && validationInfo.guest && !validationInfo.guest.allowed) || 
                        (!isComplimentary && validationInfo.member && !validationInfo.member.allowed)
                    }
                >
                    {loading ? <CircularProgress size={24} /> : 'Generate Pass'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
